import { memo, useContext, useMemo, useRef, useState, useEffect } from "react"

import {
  NodeResizeControl,
  ResizeDragEvent,
  ResizeParams,
  useReactFlow,
  useUpdateNodeInternals,
} from "reactflow"
import { uuidv7 } from "uuidv7"
import { ReactComponent as DeleteIcon } from "../../assets/icons/delete-icon.svg"
import { ReactComponent as TriangleIcon } from "../../assets/icons/triangle.svg"
import { ReactComponent as SheetHandleIcon } from "../../assets/icons/sheet-handle.svg"
import { ReactComponent as ResizeIcon } from "../../assets/icons/resize-handle.svg"
import { ReactComponent as DragColumnIcon } from "../../assets/icons/drag-columns-icon.svg"
import { ReactComponent as DragRowIcon } from "../../assets/icons/drag-row-icon.svg"
import { AppContext } from "../../AppContext"
import { UISheet, UICell, UIColumn, UINode } from "../../types/UITypes"
import "./SheetRenderer.css"
import { alphabet, CELL_WIDTH, ROW_INDEX_WIDTH } from "../../utils/constants"
import CellRenderer from "./CellRenderer"
import clsx from "clsx"
import RowIndexRenderer from "./RowIndexRenderer"

type CustomNodeProps = { id: string; data: UISheet }

const controlStyle = {
  background: "transparent",
  border: "none",
  transform: "translate(-11px, -11px)",
  zIndex: 100,
}

const HEIGHT_OFFSET = 43.5

export const SheetRenderer = ({ id, data }: CustomNodeProps) => {
  const { getNodes, screenToFlowPosition } = useReactFlow()
  const {
    selectedNodes,
    setSelectedNodes,
    removeCells,
    connectionStart,
    onConnect,
    updateCell,
    onAddColumn,
    onAddCells,
    onRemoveColumn,
    setIsEditingLabel,
    isSheetsUIVisible,
    onUpdateColumn,
    pendingSheetSelectionId,
    setPendingSheetSelectionId,
    onUpdateSheetName,
    cellWithContextMenu,
    setCellWithContextMenu,
    setEditedCell,
    setSelectedCell,
    setIsDraggingColumnOrRow,
    handleColumnOrderChange,
    handleRowOrderChange,
    multiplayerSelectedNodes,
    onCellMouseDown,
  } = useContext(AppContext)

  const isSelected = useMemo(() => selectedNodes.includes(id), [selectedNodes])
  const isConnectionModeEnabled = useMemo(
    () => !!selectedNodes.length,
    [selectedNodes]
  )

  const [sheetName, setSheetName] = useState<string>()
  const cellsContainerRef = useRef<HTMLDivElement>(null)
  const initTimeoutRef = useRef<number>()
  const offsetX = useRef<number>()

  useEffect(() => {
    setSheetName(data.name)
  }, [data])

  const rows = useMemo(() => {
    const rows: UICell[][] = []

    data.cells.forEach((cell) => {
      if (!rows[cell.rowIndex - 1]) {
        rows[cell.rowIndex - 1] = []
      }
      rows[cell.rowIndex - 1][cell.columnIndex - 1] = cell
    })

    return rows
  }, [data.cells])

  useEffect(() => {
    if (isSelected) {
      setTimeout(() => {
        setResizeState({
          initialWidth: cellsContainerRef.current?.offsetWidth,
          initialHeight: cellsContainerRef.current?.offsetHeight,
        })
      })
    } else {
      setIsDraggingColumnOrRow(false)
      setDragStarted(false)

      setDraggedColumnIndex(undefined)
      setHoveredColumnIndex(undefined)
      setDraggedColumnX(undefined)

      setDraggedRowIndex(undefined)
      setHoveredRowIndex(undefined)
      setDraggedRowY(undefined)
    }
  }, [data.cells, isSelected])

  const updateNodeInternals = useUpdateNodeInternals()

  useEffect(() => {
    if (pendingSheetSelectionId) {
      setSelectedNodes([pendingSheetSelectionId])
      setPendingSheetSelectionId(null)
    }
  }, [pendingSheetSelectionId])

  useEffect(() => {
    updateNodeInternals(id)
  }, [data])

  useEffect(() => {
    if (!initTimeoutRef.current) {
      initTimeoutRef.current = window.setTimeout(() => {
        updateNodeInternals(id)
      }, 100)
    }

    return () => {
      if (initTimeoutRef.current) {
        window.clearTimeout(initTimeoutRef.current)
      }
    }
  }, [])

  const handleEditCell = async (
    formulaEditorInlineValue: string,
    editedCell: UICell
  ) => {
    // const formulaRegExp = /^(=)([a-z]{1,})\((?!,)(.*),(.*)\)$/i
    // const formulaString = formulaEditorInlineValue.replaceAll(" ", "")
    // const formulaString = formulaEditorInlineValue

    const updatedCellName = formulaEditorInlineValue

    updateCell(updatedCellName, editedCell.id, id)

    setEditedCell(undefined)
  }

  const handleDeleteSheet = async () => {
    removeCells(data, data.cells, { removeAll: true })
  }

  const handleCellMouseUp = (event: any, rowIndex: number) => {
    // if (
    //   event.target ===
    //     cellsContainerRef.current
    //       ?.getElementsByClassName("cell")
    //       [cellIndex]?.getElementsByClassName("target-handle")[0] ||
    //   id === connectionStart?.source
    // ) {
    //   return
    // }

    if (connectionStart) {
      onConnect({
        ...connectionStart,
        target: id,
        targetHandle: `target-sheet-${data.id}-row-${rowIndex}`,
      })
    }
  }

  const getNodeTopPos = (
    cell: UICell,
    i: number,
    nodeType: string,
    edgeHandles: UINode[]
  ) => {
    if (!cellsContainerRef.current || nodeType === "target") {
      return "50%"
    }

    const cellNodes = edgeHandles?.filter(
      (edgeHandle) =>
        edgeHandle.rowIndex === cell.rowIndex &&
        edgeHandle.nodeType === nodeType
    )

    const numGaps = (cellNodes?.length || 1) - 1
    const cellHeight =
      cellsContainerRef.current.getElementsByClassName("sheet-row")[i]
        ?.clientHeight || 40
    const gap = Math.round(Math.min(cellHeight / numGaps, 8))

    return `calc(50% - ${numGaps * (gap / 2)}px + ${i * gap}px)`
  }

  const [columnNumberWithMenu, setColumnNumberWithMenu] = useState(-1)
  const [columns, setColumns] = useState<UIColumn[]>([])

  useEffect(() => {
    const updatedColumns: UIColumn[] = []
    const dataColumns = data.columns || []

    dataColumns.forEach((column) => {
      updatedColumns[column.columnIndex - 1] = {
        ...column,
        width: column.width || CELL_WIDTH,
      }
    })

    setColumns(updatedColumns)
  }, [data.columns])

  const handleClick = () => {
    setColumnNumberWithMenu(-1)
  }

  useEffect(() => {
    return document.removeEventListener("click", handleClick)
  }, [])

  const isUIVisible = isSheetsUIVisible || isSelected

  const [resizeState, setResizeState] = useState<any>({})
  const [draggedColumnIndex, setDraggedColumnIndex] = useState<number>()
  const [draggedRowIndex, setDraggedRowIndex] = useState<number>()
  const [draggedColumnX, setDraggedColumnX] = useState<number>()
  const [draggedRowY, setDraggedRowY] = useState<number>()
  const [isDragStarted, setDragStarted] = useState<boolean>()
  const [hoveredColumnIndex, setHoveredColumnIndex] = useState<number>()
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number>()
  const [rowsHeight, setRowsHeight] = useState<number[]>([])
  const [rowsYCoords, setRowsYCoords] = useState<number[]>([])

  const getResizedSheetCells = (
    width: number,
    height: number,
    roundUp: boolean
  ) => {
    let numExtraColumns = (width - resizeState.initialWidth) / CELL_WIDTH
    let numExtraRows = (height - resizeState.initialHeight) / 41
    // numExtraColumns = Math.round(numExtraColumns)
    // numExtraRows = Math.round(numExtraRows)

    numExtraColumns = roundUp
      ? Math.ceil(numExtraColumns)
      : Math.round(numExtraColumns)
    numExtraRows = roundUp ? Math.ceil(numExtraRows) : Math.round(numExtraRows)

    let updatedRows =
      numExtraColumns > 0
        ? rows.map((row, rowIndex) =>
            row.concat(
              new Array(numExtraColumns).fill(null).map(
                (_, columnIndex) =>
                  ({
                    id: uuidv7(),
                    sheetId: data.id,
                    columnIndex: row.length + columnIndex + 1,
                    rowIndex: rowIndex + 1,
                    isNew: true,
                  } as unknown as UICell)
              )
            )
          )
        : rows.map((row) => row.slice(0, row.length + numExtraColumns))

    if (numExtraRows === 0 && numExtraColumns == 0) {
      return null
    }

    updatedRows =
      numExtraRows > 0
        ? updatedRows.concat(
            new Array(numExtraRows).fill(null).map((_, rowIndex) =>
              new Array(rows[0].length + numExtraColumns).fill(null).map(
                (_, columnIndex) =>
                  ({
                    id: uuidv7(),
                    sheetId: data.id,
                    columnIndex: columnIndex + 1,
                    rowIndex: rows.length + rowIndex + 1,
                    isNew: true,
                  } as unknown as UICell)
              )
            )
          )
        : updatedRows.slice(0, rows.length + numExtraRows)

    return {
      updatedRows,
      numColumns: updatedRows.length,
      numRows: updatedRows.length,
    }
  }

  const handleResize = (
    event: ResizeDragEvent,
    params: ResizeParams & { direction: number[] }
  ) => {
    const newWidth = Math.max(params.width - 2, resizeState.initialWidth)
    const newHeight = Math.max(
      params.height - HEIGHT_OFFSET,
      resizeState.initialHeight
    )

    const resizedSheetCells = getResizedSheetCells(newWidth, newHeight, true)

    if (!resizedSheetCells) {
      return
    }

    const { updatedRows } = resizedSheetCells

    setResizeState((resizeState: any) => ({
      ...resizeState,
      width: newWidth,
      height: newHeight,
      virtualRows: updatedRows,
      isActive: true,
    }))
  }

  const handleResizeEnd = (event: ResizeDragEvent, params: ResizeParams) => {
    const newWidth = Math.max(params.width - 2, resizeState.initialWidth)
    const newHeight = Math.max(
      params.height - HEIGHT_OFFSET,
      resizeState.initialHeight
    )
    const resizedSheetCells = getResizedSheetCells(newWidth, newHeight, false)

    if (!resizedSheetCells) {
      setResizeState((resizeState: any) => ({
        ...resizeState,
        width: resizeState.initialWidth,
        height: resizeState.initialHeight,
        virtualRows: [],
        isActive: false,
      }))

      return
    }

    const { updatedRows } = resizedSheetCells
    const newCells: any = []

    updatedRows.forEach((rowCells) => {
      rowCells.forEach((cell) => {
        if (cell.isNew) {
          newCells.push(cell)
        }
      })
    })

    setResizeState((resizeState: any) => ({
      ...resizeState,
      width: newWidth,
      height: newHeight,
      virtualRows: [],
      isActive: false,
    }))

    onAddCells(data.id, newCells, getNodes())
  }

  const { sheetColumnsCount, sheetRowsCount } = useMemo(() => {
    const sheetColumnsCount = (rows?.length && rows[0].length) ?? 1
    const sheetRowsCount = rows?.length ?? 1

    return { sheetColumnsCount, sheetRowsCount }
  }, [rows])

  const handleMouseMoveX = (event: MouseEvent) => {
    if (offsetX.current === undefined) {
      return
    }

    const canvasX = screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    }).x

    let x = Math.max(canvasX - data.x - offsetX.current, ROW_INDEX_WIDTH)
    x = Math.min(x, (sheetColumnsCount - 1) * CELL_WIDTH + ROW_INDEX_WIDTH)

    setHoveredColumnIndex(
      Math.min(Math.floor(x / CELL_WIDTH), sheetColumnsCount - 1)
    )

    setDraggedColumnX(x)
  }

  const handleMouseUpX = (event: MouseEvent) => {
    if (offsetX.current === undefined) {
      return
    }

    const canvasX = screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    }).x

    let x = Math.max(event.clientX - canvasX - offsetX.current, ROW_INDEX_WIDTH)
    x = Math.min(x, (sheetColumnsCount - 1) * CELL_WIDTH + ROW_INDEX_WIDTH)

    const hoveredColumnIndex = Math.min(
      Math.floor(x / CELL_WIDTH),
      sheetColumnsCount - 1
    )

    if (draggedColumnIndex !== undefined) {
      handleColumnOrderChange(
        data.id,
        draggedColumnIndex + 1,
        hoveredColumnIndex + 1
      )
    }

    setIsDraggingColumnOrRow(false)
    setDraggedColumnIndex(undefined)
    setDraggedColumnX(undefined)
    setHoveredColumnIndex(undefined)
    setDragStarted(false)

    document.removeEventListener("mousemove", handleMouseMoveX)
    document.removeEventListener("mouseup", handleMouseUpX)
  }

  const getRowY = (rowIndex: number) => {
    return rowsHeight.reduce(
      (acc, height, i) => (i < rowIndex ? acc + height : acc),
      0
    )
  }

  const sheetHeaderHeight = 41
  const columnLabelHeight = 41
  const vertOffset = sheetHeaderHeight + columnLabelHeight

  const getHoveredRowIndex = (event: MouseEvent) => {
    if (draggedRowIndex === undefined) {
      return null
    }

    const canvasY = screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    }).y

    let y = Math.max(
      canvasY - data.y - rowsHeight[draggedRowIndex] / 2,
      sheetHeaderHeight + columnLabelHeight
    )

    y = Math.min(
      y,
      getRowY(sheetRowsCount - 1) +
        rowsHeight[sheetRowsCount - 1] -
        rowsHeight[draggedRowIndex] +
        vertOffset
    )
    setDraggedRowY(y)

    let mousePointerYRelative = canvasY - data.y - vertOffset

    if (mousePointerYRelative < 0) {
      mousePointerYRelative = 0
    }

    if (
      mousePointerYRelative >
      getRowY(sheetRowsCount - 1) + rowsHeight[sheetRowsCount - 1] / 2
    ) {
      mousePointerYRelative =
        getRowY(sheetRowsCount - 1) + rowsHeight[sheetRowsCount - 1] / 2
    }

    return Math.max(
      rowsYCoords.findIndex((rowY, i) => {
        return mousePointerYRelative < rowY + rowsHeight[i]
      }),
      0
    )
  }

  const handleMouseMoveY = (event: MouseEvent) => {
    if (draggedRowIndex === undefined) {
      return
    }

    const canvasY = screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    }).y

    let y = Math.max(
      canvasY - data.y - rowsHeight[draggedRowIndex] / 2,
      vertOffset
    )

    y = Math.min(
      y,
      getRowY(sheetRowsCount - 1) +
        rowsHeight[sheetRowsCount - 1] -
        rowsHeight[draggedRowIndex] +
        vertOffset
    )

    setDraggedRowY(y)

    const hoveredRowIndex = getHoveredRowIndex(event)

    if (hoveredRowIndex !== null) {
      setHoveredRowIndex(hoveredRowIndex)
    }
  }

  const handleMouseUpY = (event: MouseEvent) => {
    if (draggedRowIndex === undefined) {
      return
    }

    const hoveredRowIndex = getHoveredRowIndex(event)

    if (draggedRowIndex !== undefined && hoveredRowIndex !== null) {
      handleRowOrderChange(data.id, draggedRowIndex + 1, hoveredRowIndex + 1)
    }

    setIsDraggingColumnOrRow(false)
    setDraggedRowIndex(undefined)
    setDraggedRowY(undefined)
    setHoveredRowIndex(undefined)
    setDragStarted(false)

    document.removeEventListener("mousemove", handleMouseMoveY)
    document.removeEventListener("mouseup", handleMouseUpY)
  }

  const resizedColumnData = useRef<{
    startX: number
    columnIndex: number
    columns: UIColumn[]
  } | null>(null)

  const multiselectCellsData = useRef<{
    startX: number
    startY: number
    columnIndex: number
    rowIndex: number
  } | null>(null)

  const handleColumnResize = (event: MouseEvent) => {
    if (!resizedColumnData.current) {
      return
    }

    const canvasX = screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    }).x

    let diff = canvasX - resizedColumnData.current.startX
    const updatedColumnIndex = resizedColumnData.current.columnIndex
    const resizedColumn = resizedColumnData.current.columns[updatedColumnIndex]
    const updatedColumn = {
      ...resizedColumn,
      width: (resizedColumn.width ?? CELL_WIDTH) + diff,
    }

    setColumns((columns) => {
      const updatedColumns = [...columns]

      updatedColumns[updatedColumnIndex] = updatedColumn

      return updatedColumns
    })
  }

  const handleCellMouseOver = (event: any, cell: UICell) => {
    if (!multiselectCellsData.current || event.buttons !== 1) {
      return
    }

    onCellMouseDown(cell, rows, true)
  }

  const handleMultiselectHandleMouseDown = (event: any, cell: UICell) => {
    const startPos = screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    })

    multiselectCellsData.current = {
      startX: startPos.x,
      startY: startPos.y,
      columnIndex: cell.columnIndex - 1,
      rowIndex: cell.rowIndex - 1,
    }

    onCellMouseDown(cell, rows, false)
  }

  const handleColumnResizeEnd = (event: MouseEvent) => {
    setIsDraggingColumnOrRow(false)

    if (!resizedColumnData.current) {
      return
    }

    const updatedColumnIndex = resizedColumnData.current.columnIndex

    const canvasX = screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    }).x

    let diff = canvasX - resizedColumnData.current.startX

    const resizedColumn = resizedColumnData.current.columns[updatedColumnIndex]

    const updatedColumn = {
      ...resizedColumn,
      width: (resizedColumn.width ?? CELL_WIDTH) + diff,
    }

    setColumns((columns) => {
      const updatedColumns = [...columns]

      updatedColumns[updatedColumnIndex] = updatedColumn

      return updatedColumns
    })

    onUpdateColumn(data.id, updatedColumn)

    resizedColumnData.current = null
  }

  const multiplayerUser = useMemo(() => {
    return multiplayerSelectedNodes[`sheet-${data.id}`]
  }, [multiplayerSelectedNodes])

  if (!rows.length) {
    return null
  }

  return (
    <>
      <div
        key={id}
        className={clsx({
          "selected-sheet-container": isSelected,
          "ui-visible": isUIVisible,
          "disabled-sheet": !!multiplayerUser,
        })}
        style={{ position: "relative" }}
      >
        {multiplayerUser && (
          <div
            style={{
              position: "absolute",
              bottom: 0,
              right: 0,
              transform: "translateY(100%)",
            }}
          >
            Edited by: {multiplayerUser}
          </div>
        )}
        {draggedColumnIndex !== undefined ? (
          <div
            style={{
              top: 0,
              bottom: -3,
              fontSize: 12,
              width: CELL_WIDTH,
              position: "absolute",
              zIndex: 100,
              left: draggedColumnX,
            }}
            onMouseDown={(event: any) => {
              event.preventDefault()

              const canvasX = screenToFlowPosition({
                x: event.clientX,
                y: event.clientY,
              }).x

              offsetX.current =
                canvasX -
                data.x -
                draggedColumnIndex * CELL_WIDTH -
                ROW_INDEX_WIDTH

              setDragStarted(true)

              document.addEventListener("mousemove", handleMouseMoveX)
              document.addEventListener("mouseup", handleMouseUpX)
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                width: CELL_WIDTH,
              }}
            >
              <DragColumnIcon style={{ marginTop: 10 }} />
            </div>
            <div
              style={{
                top: 41,
                bottom: 0,
                width: CELL_WIDTH,
                background: "#000",
                opacity: isDragStarted ? 0.15 : 0,
                position: "absolute",
                zIndex: 100,
              }}
            />
          </div>
        ) : null}
        {draggedRowIndex !== undefined ? (
          <div
            style={{
              left: 0,
              right: 0,
              top: draggedRowY,
              height: rowsHeight[draggedRowIndex],
              position: "absolute",
              zIndex: 100,
            }}
            onMouseDown={(event: any) => {
              event.preventDefault()

              setDragStarted(true)

              document.addEventListener("mousemove", handleMouseMoveY)
              document.addEventListener("mouseup", handleMouseUpY)
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: 30,
                height: rowsHeight[draggedRowIndex],
              }}
            >
              <DragRowIcon style={{ marginLeft: -30 }} />
            </div>
            <div
              style={{
                left: 0,
                right: 0,
                top: 0,
                height: rowsHeight[draggedRowIndex],
                background: "#000",
                opacity: isDragStarted ? 0.15 : 0,
                position: "absolute",
                zIndex: 100,
              }}
            />
          </div>
        ) : null}
        <div key={id}>
          <div className="sheet-header">
            <SheetHandleIcon
              onMouseDown={() => {
                console.log("Sheet handle clicked")
                setSelectedNodes([`sheet-${data.id}`])
                setSelectedCell(undefined)
                setEditedCell(undefined)
              }}
            />
            <input
              className="column-label-input sheet-name-input"
              value={sheetName || ""}
              onChange={({ target }) => {
                setSheetName(target.value)
              }}
              onBlur={({ target }) =>
                onUpdateSheetName({ ...data, name: target.value }, target.value)
              }
            />
            <DeleteIcon className="delete-icon" onClick={handleDeleteSheet} />
          </div>
          <div
            id={`sheet-${id}`}
            ref={cellsContainerRef}
            className={isSelected ? "sheet selected-sheet" : "sheet"}
            style={
              resizeState.isActive
                ? {
                    width: resizeState.width,
                    height: resizeState.height,
                    overflow: "hidden",
                  }
                : {
                    position: "relative",
                  }
            }
          >
            {multiplayerUser && (
              <div
                className="sheet-overlay"
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  top: 0,
                  zIndex: 100,
                  background: "#000",
                  opacity: 0.1,
                  borderRadius: 11,
                }}
              />
            )}

            {isUIVisible && (
              <NodeResizeControl
                onResize={handleResize}
                onResizeEnd={handleResizeEnd}
                style={controlStyle}
                minWidth={100}
                minHeight={50}
              >
                <ResizeIcon style={{ width: 21, height: 21 }} />
              </NodeResizeControl>
            )}
            {isUIVisible && (
              <div className="sheet-row">
                <div className="cell" style={{ width: 30, maxWidth: 30 }}></div>
                {rows[0].map((cell: UICell, i) => (
                  <div
                    key={`cell-label-${cell.id}`}
                    id={`cell-label-${cell.id}`}
                    className={clsx("cell", "header-cell", {
                      "highlighted-cell": false,
                    })}
                    onMouseOver={() => {}}
                    onMouseOut={() => {}}
                    onMouseUp={() => {}}
                    style={{ width: columns[i]?.width || CELL_WIDTH }}
                  >
                    <div
                      className="column-resizer"
                      onMouseOver={() => {
                        setIsDraggingColumnOrRow(true)
                      }}
                      onMouseOut={() => {
                        if (resizedColumnData !== null) {
                          setIsDraggingColumnOrRow(false)
                        }
                      }}
                      onMouseDown={(event: any) => {
                        event.preventDefault()

                        const startX = screenToFlowPosition({
                          x: event.clientX,
                          y: event.clientY,
                        }).x

                        resizedColumnData.current = {
                          startX,
                          columnIndex: cell.columnIndex - 1,
                          columns,
                        }

                        document.addEventListener(
                          "mousemove",
                          handleColumnResize
                        )
                        document.addEventListener(
                          "mouseup",
                          handleColumnResizeEnd
                        )
                      }}
                    ></div>
                    <div style={{ padding: 10 }}>
                      {alphabet[i]}
                      <input
                        className="column-label-input"
                        value={columns[i]?.text || ""}
                        onChange={({ target }) => {
                          const updatedColumns: UIColumn[] = [...columns]

                          updatedColumns[i].text = target.value
                          setColumns(updatedColumns)
                        }}
                        onFocus={() => setIsEditingLabel(true)}
                        onBlur={({ target }) => {
                          onUpdateColumn(data.id, {
                            ...data.columns[i],
                            text: target.value,
                          })
                          setIsEditingLabel(false)
                        }}
                      />
                      <div
                        className="menu-icon"
                        onClick={(event) => {
                          event.preventDefault()
                          event.stopPropagation()
                          document.addEventListener("click", handleClick)
                          setColumnNumberWithMenu(i)
                        }}
                      >
                        <TriangleIcon className="dropdown-triangle" />
                      </div>
                      {columnNumberWithMenu === i && (
                        <div key={cell.id} className="cell-menu">
                          <div
                            className="cell-menu-item"
                            onClick={() =>
                              onAddColumn(data.id, cell.columnIndex)
                            }
                          >
                            Add Column
                          </div>
                          <div
                            className="cell-menu-item"
                            onClick={() =>
                              onRemoveColumn(data.id, cell.columnIndex)
                            }
                          >
                            Delete Column
                          </div>
                          <div
                            className="cell-menu-item"
                            onClick={() => {
                              setDraggedColumnIndex(i)
                              setDraggedColumnX(
                                i * CELL_WIDTH + ROW_INDEX_WIDTH
                              )
                              setIsDraggingColumnOrRow(true)
                            }}
                          >
                            Move Column
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!isUIVisible && (
              <div className="sheet-row">
                {(resizeState.virtualRows?.length
                  ? resizeState.virtualRows
                  : rows)[0].map((cell: UICell, i: number) => (
                  <div
                    key={`column-label-preview-${cell.id}`}
                    id={`column-label-preview-${cell.id}`}
                    className="cell header-cell"
                    style={{ width: columns[i]?.width || CELL_WIDTH }}
                  >
                    <span style={{ paddingLeft: 10, paddingRight: 10 }}>
                      {columns[i]?.text || <span>&nbsp;</span>}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {(resizeState.virtualRows?.length
              ? resizeState.virtualRows
              : rows
            ).map((rowCells: UICell[], rowIndex: number) => (
              <div key={`${data.id}-row-${rowIndex}`} className="sheet-row">
                {isUIVisible && (
                  <RowIndexRenderer
                    sheet={data}
                    cell={rowCells[0]}
                    getNodeTopPos={getNodeTopPos}
                    onCellMouseUp={handleCellMouseUp}
                    connectionModeEnabled={isConnectionModeEnabled}
                    onCellContextMenu={(
                      cellId: string,
                      target: "rowIndex" | "cell"
                    ) => setCellWithContextMenu({ cellId, target })}
                    cellWithContextMenu={cellWithContextMenu}
                    renderHandles={!resizeState?.isActive}
                    onMoveRow={(rowIndex: number) => {
                      setDraggedRowIndex(rowIndex - 1)

                      const rowsHeight = Array.from(
                        document.querySelectorAll(`#sheet-${id} > .sheet-row`)
                      )
                        .slice(1)
                        .map((row) => row.clientHeight)

                      setRowsHeight(rowsHeight)

                      const rowsYCoords = rowsHeight.reduce(
                        (acc, row, i) => [
                          ...acc,
                          i > 0 ? acc[i - 1] + rowsHeight[i - 1] : 0,
                        ],
                        [] as number[]
                      )

                      setRowsYCoords(rowsYCoords)

                      setDraggedRowY(rowsYCoords[rowIndex - 1] + vertOffset)
                      setCellWithContextMenu(undefined)
                      setIsDraggingColumnOrRow(true)
                    }}
                  />
                )}
                {rowCells.map((cell: UICell) => (
                  <CellRenderer
                    key={`cell-renderer-${cell.id}`}
                    cell={cell}
                    onCellMouseUp={handleCellMouseUp}
                    isSheetSelected={isSelected}
                    onEditCell={handleEditCell}
                    sheet={data}
                    rowSize={rowCells.length}
                    getNodeTopPos={getNodeTopPos}
                    connectionModeEnabled={isConnectionModeEnabled}
                    onAddColumn={onAddColumn}
                    resizeState={resizeState}
                    showColumnContextMenu={true}
                    showRowContextMenu={true}
                    sheetColumnsCount={sheetColumnsCount}
                    sheetRowsCount={sheetRowsCount}
                    sheetRows={rows}
                    draggedColumnIndex={draggedColumnIndex}
                    hoveredColumnIndex={hoveredColumnIndex}
                    draggedRowIndex={
                      draggedRowIndex !== undefined
                        ? draggedRowIndex + 1
                        : draggedRowIndex
                    }
                    hoveredRowIndex={
                      hoveredRowIndex !== undefined
                        ? hoveredRowIndex + 1
                        : hoveredRowIndex
                    }
                    width={columns[cell.columnIndex - 1]?.width || CELL_WIDTH}
                    onMultiselectHandleMouseDown={
                      handleMultiselectHandleMouseDown
                    }
                    onCellMouseOver={handleCellMouseOver}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
        {/* {isUIVisible && (
          <div
            className={clsx("sheet-footer", {
              "sheet-footer-disabled": isPendingApiResponse,
            })}
          >
            <button
              onClick={() => handleAddColumn(data.id, rows[0].length)}
              className="sheet-footer-button"
            >
              <PlusIcon />
              <ArrowRightIcon className="sheet-footer-arrow-right-icon sheet-footer-arrow-icon" />
            </button>
            <button
              onClick={() => handleAddRow(data.id, rows.length)}
              className="sheet-footer-button"
            >
              <PlusIcon />
              <ArrowDownIcon className="sheet-footer-arrow-down-icon sheet-footer-arrow-icon" />
            </button>
          </div>
        )} */}
      </div>
    </>
  )
}

export default memo(SheetRenderer)
